import * as PIXI from 'pixi.js';
import { PianoKeyboard } from './PianoKeyboard';

/**
 * Simplified note data for the falling notes visualization.
 */
export interface BambooNote {
  noteId: string;
  midi: number;
  startTime: number;  // absolute start time in ms
  endTime: number;    // absolute end time in ms
  hand: number;       // 0 = RIGHT (treble), 1 = LEFT (bass)
  measureIndex: number;
}

// Colors
const RIGHT_HAND_COLOR = 0x4a90d9;    // blue
const LEFT_HAND_COLOR = 0xd97a4a;     // orange
const SUCCESS_COLOR = 0x33aa55;       // green
const FAIL_COLOR = 0xdd3333;          // red
const HIT_ZONE_COLOR = 0xffffff;
const BG_COLOR = 0x1a1a2e;

// Layout
const KEYBOARD_HEIGHT_RATIO = 0.12;   // keyboard takes 12% of canvas height
const MIN_KEYBOARD_HEIGHT = 80;       // minimum keyboard height in px (for split mode)
const HIT_ZONE_Y_RATIO = 0.85;       // hit zone at 85% from top
const LOOKAHEAD_MS = 4000;            // show notes 4 seconds ahead
const NOTE_CORNER_RADIUS = 4;

// Timing
const LERP_SPEED = 0.15;
const JUMP_THRESHOLD_MS = 500;
const PASS_GRACE_MS = 200;
const VIEW_BUFFER_MS = 500;

// Hit effects
const EFFECT_DURATION = 20;        // frames (~333ms at 60fps)
const EFFECT_GLOW_EXPAND = 8;     // px expansion per side for glow

export class FallingNotesRenderer {
  private app: PIXI.Application | null = null;
  private keyboard: PianoKeyboard | null = null;

  // Containers (layered: bg -> notes -> effects -> hitzone -> keyboard -> overlay)
  private bgContainer: PIXI.Container | null = null;
  private notesContainer: PIXI.Container | null = null;
  private effectsContainer: PIXI.Container | null = null;
  private hitZoneContainer: PIXI.Container | null = null;
  private overlayContainer: PIXI.Container | null = null;

  // Hit flash effects
  private activeEffects: { g: PIXI.Graphics; life: number }[] = [];

  // Note data
  private allNotes: BambooNote[] = [];
  private noteGraphics: Map<string, PIXI.Graphics> = new Map();
  private noteStates: Map<string, 'pending' | 'success' | 'fail'> = new Map();

  // Graphics pool for recycling
  private graphicsPool: PIXI.Graphics[] = [];

  // Position
  private currentTime = 0;
  private targetTime = 0;

  // Wall-clock extrapolation for smooth scrolling during playback
  private isPlaying = false;
  private musicRate = 0;           // music ms per wall ms (speed / 7.5)
  private anchorWallTime = 0;      // performance.now() at last position change
  private anchorMusicTime = 0;     // music time at last position change

  // Layout dimensions
  private canvasWidth = 0;
  private canvasHeight = 0;
  private hitZoneY = 0;
  private pixelsPerMs = 0;
  private keyboardTopY = 0;

  // State
  private initialized = false;
  private destroyed = false;

  async init(container: HTMLElement): Promise<void> {
    if (this.destroyed) return;

    this.app = new PIXI.Application();
    await this.app.init({
      resizeTo: container,
      backgroundColor: BG_COLOR,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });

    container.appendChild(this.app.canvas);

    this.canvasWidth = this.app.screen.width;
    this.canvasHeight = this.app.screen.height;

    // Create layered containers
    this.bgContainer = new PIXI.Container();
    this.notesContainer = new PIXI.Container();
    this.effectsContainer = new PIXI.Container();
    this.hitZoneContainer = new PIXI.Container();
    this.overlayContainer = new PIXI.Container();

    this.app.stage.addChild(this.bgContainer);
    this.app.stage.addChild(this.notesContainer);
    this.app.stage.addChild(this.effectsContainer);
    this.app.stage.addChild(this.hitZoneContainer);
    this.app.stage.addChild(this.overlayContainer);

    this.computeLayout();
    this.drawHitZone();

    // Create piano keyboard
    this.keyboard = new PianoKeyboard(this.app.stage);
    const kbHeight = Math.max(this.canvasHeight * KEYBOARD_HEIGHT_RATIO, MIN_KEYBOARD_HEIGHT);
    this.keyboard.draw(0, this.keyboardTopY, this.canvasWidth, kbHeight);

    // Start render loop
    this.app.ticker.add(this.onTick, this);

    this.initialized = true;
  }

  private computeLayout() {
    // Ensure keyboard has a usable minimum height (important for split mode)
    const kbHeight = Math.max(this.canvasHeight * KEYBOARD_HEIGHT_RATIO, MIN_KEYBOARD_HEIGHT);
    // Position hit zone above the keyboard
    this.hitZoneY = this.canvasHeight - kbHeight - 4;
    this.keyboardTopY = this.hitZoneY + 4;
    this.pixelsPerMs = LOOKAHEAD_MS > 0
      ? this.hitZoneY / LOOKAHEAD_MS
      : 0.1;
  }

  private drawHitZone() {
    const line = new PIXI.Graphics();
    line.rect(0, this.hitZoneY - 1, this.canvasWidth, 3);
    line.fill({ color: HIT_ZONE_COLOR, alpha: 0.4 });
    this.hitZoneContainer?.addChild(line);
  }

  /**
   * Load notes from the timemap. Call this when a sample is loaded.
   */
  loadNotes(notes: BambooNote[]) {
    this.allNotes = notes.sort((a, b) => a.startTime - b.startTime);
    this.noteStates.clear();
    this.clearNoteGraphics();

    for (const note of this.allNotes) {
      this.noteStates.set(note.noteId, 'pending');
    }
  }

  /**
   * Update the current playback position. Called by the sync hook.
   * Only updates the extrapolation anchor when the position actually changes,
   * so repeated calls with the same value don't reset the clock.
   * Detects backwards jumps (reprises) and resets notes in the replayed range.
   */
  setPosition(timeMs: number) {
    if (Math.abs(timeMs - this.targetTime) > 1) {
      // Detect backwards jump (reprise) — reset notes that will be replayed
      // so they show as pending again and produce hit effects on the second pass
      if (this.targetTime - timeMs > JUMP_THRESHOLD_MS) {
        this.resetNotesInRange(timeMs, this.targetTime);
      }

      this.anchorWallTime = performance.now();
      this.anchorMusicTime = timeMs;
      this.targetTime = timeMs;
    }
  }

  /**
   * Reset notes within a time range back to 'pending'.
   * Used when a reprise jumps playback backwards so notes can be replayed.
   */
  private resetNotesInRange(fromTime: number, toTime: number) {
    for (const note of this.allNotes) {
      if (note.startTime > toTime) break; // allNotes sorted by startTime
      if (note.startTime >= fromTime) {
        this.noteStates.set(note.noteId, 'pending');
      }
    }
    // Clear cached graphics for reset notes so they redraw with pending color
    for (const [noteId, g] of this.noteGraphics) {
      const state = this.noteStates.get(noteId);
      if (state === 'pending') {
        g.clear();
      }
    }
  }

  /**
   * Update playback state for smooth scrolling.
   * In play mode, notes scroll at a constant rate derived from timer.speed.
   * In practice mode, notes use LERP to snap to position on user input.
   */
  setPlaybackState(playing: boolean, speed: number) {
    if (playing && !this.isPlaying) {
      // Just started playing — anchor from current position
      this.anchorWallTime = performance.now();
      this.anchorMusicTime = this.currentTime;
    }
    this.isPlaying = playing;
    this.musicRate = speed / 7.5;
  }

  /**
   * Mark a note as successfully played by noteId.
   */
  onNoteSuccess(noteId: string) {
    this.noteStates.set(noteId, 'success');
  }

  /**
   * Mark a note as failed by noteId.
   */
  onNoteFail(noteId: string) {
    this.noteStates.set(noteId, 'fail');
  }

  /**
   * Mark the nearest pending note matching the given MIDI pitch as success.
   * Searches near the current time to find the right note.
   */
  onNoteSuccessByPitch(midi: number) {
    let best: BambooNote | null = null;
    let bestDist = Infinity;
    for (const note of this.allNotes) {
      if (note.midi !== midi) continue;
      const state = this.noteStates.get(note.noteId);
      if (state !== 'pending') continue;
      const dist = Math.abs(note.startTime - this.currentTime);
      if (dist < bestDist) {
        bestDist = dist;
        best = note;
      }
    }
    if (best) {
      this.noteStates.set(best.noteId, 'success');
    }
  }

  /**
   * Mark the nearest pending note matching the given MIDI pitch as fail.
   */
  onNoteFailByPitch(midi: number) {
    let best: BambooNote | null = null;
    let bestDist = Infinity;
    for (const note of this.allNotes) {
      if (note.midi !== midi) continue;
      const state = this.noteStates.get(note.noteId);
      if (state !== 'pending') continue;
      const dist = Math.abs(note.startTime - this.currentTime);
      if (dist < bestDist) {
        bestDist = dist;
        best = note;
      }
    }
    if (best) {
      this.noteStates.set(best.noteId, 'fail');
    }
  }

  /**
   * Mark all pending notes in a given measure as success.
   * Used when NOTE_SUCCESS fires with { measureIndex }.
   */
  onMeasureNotesSuccess(measureIndex: number) {
    for (const note of this.allNotes) {
      if (note.measureIndex === measureIndex) {
        const state = this.noteStates.get(note.noteId);
        if (state === 'pending') {
          this.noteStates.set(note.noteId, 'success');
          this.spawnHitEffect(note.midi, SUCCESS_COLOR);
        }
      }
    }
  }

  /**
   * Mark the nearest pending note in a given measure as fail.
   */
  onMeasureNotesFail(measureIndex: number) {
    for (const note of this.allNotes) {
      if (note.measureIndex === measureIndex) {
        const state = this.noteStates.get(note.noteId);
        if (state === 'pending') {
          this.noteStates.set(note.noteId, 'fail');
          this.spawnHitEffect(note.midi, FAIL_COLOR);
          break;
        }
      }
    }
  }

  /**
   * Spawn a glowing flash effect at the hit zone for a given MIDI key.
   * Creates the Graphics once; animation uses alpha/scale transforms only
   * (avoids PIXI v8 issues with repeated clear/rebuild cycles).
   */
  spawnHitEffect(midi: number, color: number) {
    const keyPos = this.keyboard?.getKeyPosition(midi);
    if (!keyPos || !this.effectsContainer) return;

    const g = new PIXI.Graphics();
    const w = keyPos.width + EFFECT_GLOW_EXPAND * 2;
    const h = 24;

    // Draw centered at origin so scale works symmetrically
    g.roundRect(-w / 2, -h / 2, w, h, 6);
    g.fill({ color, alpha: 1 });

    // Position via transform
    g.x = keyPos.centerX;
    g.y = this.hitZoneY;
    g.alpha = 0.9;

    this.effectsContainer.addChild(g);
    this.activeEffects.push({ g, life: EFFECT_DURATION });
  }

  /**
   * Highlight a piano key and spawn a hit flash.
   */
  pressKey(midi: number) {
    this.keyboard?.pressKey(midi);
    this.spawnHitEffect(midi, HIT_ZONE_COLOR);
  }

  /**
   * Release a piano key highlight.
   */
  releaseKey(midi: number) {
    this.keyboard?.releaseKey(midi);
  }

  /**
   * Main render loop — called every frame by PIXI ticker.
   */
  private onTick = () => {
    if (this.destroyed || !this.initialized) return;

    if (this.isPlaying && this.musicRate > 0) {
      // Play mode: constant-rate extrapolation from last anchor point.
      // This gives smooth scrolling even though runner.measure only
      // updates discretely (per-note).
      const now = performance.now();
      const wallDelta = now - this.anchorWallTime;
      this.currentTime = this.anchorMusicTime + wallDelta * this.musicRate;
    } else {
      // Practice mode: LERP toward target position.
      // Notes wait for user input, then smooth-transition on advance.
      const diff = this.targetTime - this.currentTime;
      if (Math.abs(diff) < 1) {
        this.currentTime = this.targetTime;
      } else if (Math.abs(diff) > JUMP_THRESHOLD_MS) {
        this.currentTime = this.targetTime;
      } else {
        this.currentTime += diff * LERP_SPEED;
      }
    }

    this.autoMarkPassedNotes();
    this.renderNotes();
    this.updateEffects();
  };

  /**
   * Auto-mark notes as "success" once they've scrolled past the hit zone.
   * In practice mode, cursor only advances when you play correctly,
   * so any note behind the cursor was necessarily played.
   */
  private autoMarkPassedNotes() {
    const passThreshold = this.currentTime - PASS_GRACE_MS;
    for (const note of this.allNotes) {
      if (note.startTime > passThreshold) break;
      const state = this.noteStates.get(note.noteId);
      if (state === 'pending') {
        this.noteStates.set(note.noteId, 'success');
        this.spawnHitEffect(note.midi, SUCCESS_COLOR);
      }
    }
  }

  /**
   * Update hit flash effects — fade out and remove expired ones.
   * Uses alpha/scale transforms only (no geometry rebuild per frame).
   */
  private updateEffects() {
    for (let i = this.activeEffects.length - 1; i >= 0; i--) {
      const effect = this.activeEffects[i];
      effect.life--;

      if (effect.life <= 0) {
        this.effectsContainer?.removeChild(effect.g);
        if (!effect.g.destroyed) effect.g.destroy();
        this.activeEffects.splice(i, 1);
        continue;
      }

      const progress = 1 - effect.life / EFFECT_DURATION;
      effect.g.alpha = 0.9 * (1 - progress);
      const scale = 1 + progress * 0.4;
      effect.g.scale.set(scale, scale);
    }
  }

  /**
   * Render visible notes as falling bars.
   *
   * Y-coordinate model:
   *   - Notes with startTime in the future are ABOVE the hit zone (lower Y value)
   *   - Notes with startTime in the past are BELOW the hit zone (higher Y value)
   *   - timeToY(t) = hitZoneY - (t - currentTime) * pixelsPerMs
   *   - A note bar spans from timeToY(endTime) at the top to timeToY(startTime) at the bottom
   *     because endTime > startTime, so endTime maps to a smaller Y (higher on screen)
   */
  private renderNotes() {
    const viewStart = this.currentTime - VIEW_BUFFER_MS;
    const viewEnd = this.currentTime + LOOKAHEAD_MS;

    const visibleIds = new Set<string>();

    for (const note of this.allNotes) {
      if (note.endTime < viewStart || note.startTime > viewEnd) continue;

      // Check if we can position this note BEFORE creating graphics
      const keyPos = this.keyboard?.getKeyPosition(note.midi);
      if (!keyPos) continue;

      visibleIds.add(note.noteId);

      let g = this.noteGraphics.get(note.noteId);
      if (!g) {
        g = this.acquireGraphics();
        this.noteGraphics.set(note.noteId, g);
        this.notesContainer?.addChild(g);
      }

      // Y calculation:
      // timeToY converts a timestamp to screen Y position
      // Future time = smaller Y (above hit zone), past time = larger Y (below)
      const startY = this.hitZoneY - (note.startTime - this.currentTime) * this.pixelsPerMs;
      const endY = this.hitZoneY - (note.endTime - this.currentTime) * this.pixelsPerMs;
      // endTime > startTime → endY < startY
      // So endY is the visual TOP, startY is the visual BOTTOM
      const noteTop = endY;
      const noteHeight = Math.max(startY - endY, 3);

      // Determine color
      const state = this.noteStates.get(note.noteId) || 'pending';
      let color: number;
      if (state === 'success') {
        color = SUCCESS_COLOR;
      } else if (state === 'fail') {
        color = FAIL_COLOR;
      } else {
        color = note.hand === 0 ? RIGHT_HAND_COLOR : LEFT_HAND_COLOR;
      }

      // Draw
      const barWidth = keyPos.width * 0.85;
      const barX = keyPos.centerX - barWidth / 2;

      g.clear();
      g.roundRect(barX, noteTop, barWidth, noteHeight, NOTE_CORNER_RADIUS);
      g.fill({ color, alpha: state === 'success' ? 0.5 : 0.9 });
    }

    // Remove graphics for notes that left the viewport
    for (const [noteId, g] of this.noteGraphics) {
      if (!visibleIds.has(noteId)) {
        this.notesContainer?.removeChild(g);
        this.releaseGraphics(g);
        this.noteGraphics.delete(noteId);
      }
    }
  }

  /**
   * Get a Graphics from the pool or create new.
   */
  private acquireGraphics(): PIXI.Graphics {
    while (this.graphicsPool.length > 0) {
      const g = this.graphicsPool.pop()!;
      if (!g.destroyed) return g;
    }
    return new PIXI.Graphics();
  }

  /**
   * Return a Graphics to the pool.
   */
  private releaseGraphics(g: PIXI.Graphics) {
    g.clear();
    this.graphicsPool.push(g);
  }

  private clearNoteGraphics() {
    for (const [, g] of this.noteGraphics) {
      this.notesContainer?.removeChild(g);
      if (!g.destroyed) g.destroy();
    }
    this.noteGraphics.clear();
    this.graphicsPool.forEach(g => {
      if (!g.destroyed) g.destroy();
    });
    this.graphicsPool = [];

    // Clear active effects
    for (const effect of this.activeEffects) {
      this.effectsContainer?.removeChild(effect.g);
      if (!effect.g.destroyed) effect.g.destroy();
    }
    this.activeEffects = [];
  }

  /**
   * Handle resize.
   */
  resize() {
    if (!this.app || this.destroyed) return;

    // Pause ticker during resize
    this.app.ticker.remove(this.onTick, this);

    // Force PIXI to re-measure the container (CSS changes don't trigger auto-resize)
    this.app.resize();

    this.canvasWidth = this.app.screen.width;
    this.canvasHeight = this.app.screen.height;

    this.computeLayout();

    // Redraw hit zone
    this.hitZoneContainer?.removeChildren();
    this.drawHitZone();

    // Redraw keyboard
    if (this.keyboard) {
      this.keyboard.destroy();
      this.keyboard = new PianoKeyboard(this.app.stage);
      const kbHeight = Math.max(this.canvasHeight * KEYBOARD_HEIGHT_RATIO, MIN_KEYBOARD_HEIGHT);
      this.keyboard.draw(0, this.keyboardTopY, this.canvasWidth, kbHeight);
    }

    // Clear note graphics (they'll be re-created on next tick)
    this.clearNoteGraphics();

    // Resume ticker
    this.app.ticker.add(this.onTick, this);
  }

  /**
   * Reset state (e.g. when rewinding).
   */
  reset() {
    this.currentTime = 0;
    this.targetTime = 0;
    this.anchorWallTime = 0;
    this.anchorMusicTime = 0;
    this.isPlaying = false;
    this.noteStates.clear();
    for (const note of this.allNotes) {
      this.noteStates.set(note.noteId, 'pending');
    }
    this.clearNoteGraphics();
    this.keyboard?.releaseAll();
  }

  destroy() {
    this.destroyed = true;
    this.initialized = false;
    this.clearNoteGraphics();
    this.keyboard?.destroy();
    this.keyboard = null;

    this.noteStates.clear();
    this.allNotes = [];
    this.currentTime = 0;
    this.targetTime = 0;

    if (this.app) {
      try {
        this.app.ticker?.remove(this.onTick, this);
        this.app.destroy(true, { children: true });
      } catch {
        // PIXI app may be partially destroyed during HMR
      }
      this.app = null;
    }

    this.bgContainer = null;
    this.notesContainer = null;
    this.effectsContainer = null;
    this.hitZoneContainer = null;
    this.overlayContainer = null;
  }
}
